import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coin } from "../components/ui/Coin.js";
import { Icon } from "../components/ui/Icon.js";
import {
  fetchScannerTop,
  type ScannerItem,
  type ScannerPayload,
} from "../lib/api.js";
import { useChartStore, type Exchange, type Timeframe } from "../lib/chartStore.js";
import { marketSocket } from "../lib/ws.js";

function scoreColor(score: number): string {
  if (score >= 80) return "var(--buy)";
  if (score >= 60) return "var(--warn)";
  return "var(--text-faint)";
}

function regimeColor(regime: string | null): string {
  if (!regime) return "var(--text-muted)";
  if (regime.includes("UP")) return "var(--buy)";
  if (regime.includes("DOWN")) return "var(--sell)";
  if (regime.includes("VOLATIL")) return "var(--warn)";
  return "var(--text-muted)";
}

function ageOf(ms: number): string {
  const seconds = Math.max(0, (Date.now() - ms) / 1000);
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.floor(minutes)}m`;
  return `${(minutes / 60).toFixed(1)}h`;
}

function coinSym(symbol: string): string {
  return symbol.replace(/USDT$|USD$|USDC$/, "");
}

interface FiltersState {
  minScore: number;
  minConfidence: number;
  exchanges: Set<string>;
  timeframes: Set<string>;
}

const ALL_EXCHANGES = [
  "binance",
  "bybit",
  "okx",
  "kraken",
  "coinbase",
  "kucoin",
  "gateio",
  "bitget",
];
const ALL_TFS = ["1m", "5m", "15m", "1h", "4h", "1d"];

function defaultFilters(): FiltersState {
  return {
    minScore: 60,
    minConfidence: 0.55,
    exchanges: new Set(ALL_EXCHANGES),
    timeframes: new Set(ALL_TFS),
  };
}

export function ScannerPage() {
  const navigate = useNavigate();
  const setAll = useChartStore((s) => s.setAll);
  const [data, setData] = useState<ScannerPayload | null>(null);
  const [filters, setFilters] = useState<FiltersState>(defaultFilters);

  const initial = useQuery({
    queryKey: ["scanner-top"],
    queryFn: fetchScannerTop,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (initial.data) setData(initial.data);
  }, [initial.data]);

  useEffect(
    () =>
      marketSocket.subscribe("scanner:top", (_c, payload) => {
        setData(payload as ScannerPayload);
      }),
    [],
  );

  const items = useMemo<ScannerItem[]>(() => {
    if (!data?.items) return [];
    return data.items.filter((it) => {
      if (it.compositeScore < filters.minScore) return false;
      if (it.confidence < filters.minConfidence) return false;
      if (!filters.exchanges.has(it.exchange)) return false;
      if (!filters.timeframes.has(it.timeframe)) return false;
      return true;
    });
  }, [data, filters]);

  function toggle<K extends keyof Omit<FiltersState, "minScore" | "minConfidence">>(
    key: K,
    value: string,
  ): void {
    setFilters((cur) => {
      const next = new Set(cur[key]);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...cur, [key]: next };
    });
  }

  function selectItem(it: ScannerItem): void {
    setAll({
      symbol: it.symbol,
      exchange: it.exchange as Exchange,
      timeframe: it.timeframe as Timeframe,
    });
    navigate("/");
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          padding: "12px 18px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Opportunity Scanner</div>
          <div className="muted" style={{ fontSize: 11 }}>
            Top-N ranked spot BUY entries · refreshes every 10s
          </div>
        </div>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-muted)" }}>
          {items.length} of {data?.items.length ?? 0} opportunities
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <aside
          style={{
            width: 240,
            borderRight: "1px solid var(--border)",
            padding: 16,
            overflowY: "auto",
            flexShrink: 0,
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, marginBottom: 6, color: "var(--text-mid)" }}>
              Min composite score
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={filters.minScore}
              onChange={(e) =>
                setFilters({ ...filters, minScore: Number(e.target.value) })
              }
              style={{ width: "100%", accentColor: "var(--primary)" }}
            />
            <div className="mono tnum" style={{ fontSize: 11, color: "var(--text-mid)" }}>
              ≥ {filters.minScore}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, marginBottom: 6, color: "var(--text-mid)" }}>
              Min confidence
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round(filters.minConfidence * 100)}
              onChange={(e) =>
                setFilters({ ...filters, minConfidence: Number(e.target.value) / 100 })
              }
              style={{ width: "100%", accentColor: "var(--ai)" }}
            />
            <div className="mono tnum" style={{ fontSize: 11, color: "var(--text-mid)" }}>
              ≥ {(filters.minConfidence * 100).toFixed(0)}%
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, marginBottom: 6, color: "var(--text-mid)" }}>
              Exchange
            </div>
            {ALL_EXCHANGES.map((ex) => (
              <label
                key={ex}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  fontSize: 12,
                  padding: "3px 0",
                }}
              >
                <input
                  type="checkbox"
                  checked={filters.exchanges.has(ex)}
                  onChange={() => toggle("exchanges", ex)}
                  style={{ accentColor: "var(--primary)" }}
                />
                {ex}
              </label>
            ))}
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, marginBottom: 6, color: "var(--text-mid)" }}>
              Timeframe
            </div>
            {ALL_TFS.map((tf) => (
              <label
                key={tf}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  fontSize: 12,
                  padding: "3px 0",
                }}
              >
                <input
                  type="checkbox"
                  checked={filters.timeframes.has(tf)}
                  onChange={() => toggle("timeframes", tf)}
                  style={{ accentColor: "var(--ai)" }}
                />
                {tf}
              </label>
            ))}
          </div>

          <button
            type="button"
            className="se-btn"
            style={{ width: "100%", marginTop: 4 }}
            onClick={() => setFilters(defaultFilters())}
          >
            Reset filters
          </button>
        </aside>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {items.length === 0 ? (
            <div style={{ padding: 32 }}>
              <div className="se-card" style={{ padding: 18 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>No opportunities</div>
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                  Adjust the filters or wait for new ranked signals to arrive.
                </div>
              </div>
            </div>
          ) : (
            <table className="se-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Symbol</th>
                  <th>Exchange</th>
                  <th>TF</th>
                  <th>Composite</th>
                  <th>Confidence</th>
                  <th>Regime</th>
                  <th>Entry</th>
                  <th>SL</th>
                  <th>Age</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((o, idx) => {
                  const rank = idx + 1;
                  return (
                    <tr
                      key={o.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => selectItem(o)}
                    >
                      <td>
                        <span
                          className="mono tnum"
                          style={{
                            fontSize: 12,
                            fontWeight: rank <= 3 ? 600 : 400,
                            color: rank <= 3 ? "var(--ai)" : "var(--text-faint)",
                          }}
                        >
                          {String(rank).padStart(2, "0")}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Coin sym={coinSym(o.symbol)} size="sm" />
                          <span style={{ fontWeight: 500 }}>{o.symbol}</span>
                        </div>
                      </td>
                      <td className="muted">{o.exchange}</td>
                      <td>
                        <span className="se-tag">{o.timeframe}</span>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span
                            className="mono tnum"
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: scoreColor(o.compositeScore),
                              width: 28,
                            }}
                          >
                            {Math.round(o.compositeScore)}
                          </span>
                          <div className="se-progress" style={{ width: 50 }}>
                            <div
                              className="se-progress-bar"
                              style={{
                                width: `${o.compositeScore}%`,
                                background: scoreColor(o.compositeScore),
                              }}
                            />
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span
                            className="mono tnum ai-text"
                            style={{ fontSize: 12, fontWeight: 500, width: 32 }}
                          >
                            {Math.round(o.confidence * 100)}%
                          </span>
                          <div className="se-progress" style={{ width: 40 }}>
                            <div
                              className="se-progress-bar ai"
                              style={{ width: `${o.confidence * 100}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: "0.04em",
                          color: regimeColor(o.marketRegime),
                        }}
                      >
                        {o.marketRegime ?? "—"}
                      </td>
                      <td className="num">
                        {o.entryPrice.toLocaleString("en", { maximumFractionDigits: 4 })}
                      </td>
                      <td className="num down">
                        {o.stopLoss.toLocaleString("en", { maximumFractionDigits: 4 })}
                      </td>
                      <td className="muted num" style={{ fontSize: 11 }}>
                        {ageOf(o.issuedAt)}
                      </td>
                      <td>
                        <button type="button" className="se-btn se-btn-sm">
                          Open <Icon name="chevronRight" size={11} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
